import BaseModal from 'components/Modals/BaseModal';
import { ProgressBar } from 'react-bootstrap';
import type { BulkAddProgress } from '../../utils/bulkDevices.utils';

interface BulkAddProgressModalProps {
  show: boolean;
  progress: BulkAddProgress | null;
  onClose: () => void;
}

/**
 * Modal that displays sequential bulk-add progress for USGS devices.
 *
 * Shows a Bootstrap ProgressBar, per-device error details, and a Done
 * button once all devices have been processed. Cannot be dismissed while
 * the add is still in progress.
 */
export default function BulkAddProgressModal({
  show,
  progress,
  onClose,
}: BulkAddProgressModalProps) {
  if (!progress) return null;

  const { total, current, succeeded, failed, errors, isComplete } = progress;
  const percentage = total > 0 ? Math.round((current / total) * 100) : 0;
  const progressVariant = failed > 0 ? 'warning' : 'success';

  return (
    <BaseModal
      show={show}
      handleShow={isComplete ? onClose : undefined}
      closeButton={isComplete}
      title="Adding USGS Devices"
      size="md"
    >
      <div className="d-flex flex-column gap-3">
        {/* Progress bar */}
        <div>
          <div className="d-flex justify-content-between mb-1 small text-muted">
            <span>
              {current} of {total} processed
            </span>
            <span>{percentage}%</span>
          </div>
          <ProgressBar
            now={percentage}
            variant={progressVariant}
            animated={!isComplete}
            striped={!isComplete}
          />
        </div>

        {/* Counts */}
        <div className="d-flex gap-4">
          <span className="text-success fw-semibold">{succeeded} added</span>
          {failed > 0 && <span className="text-warning fw-semibold">{failed} failed</span>}
        </div>

        {/* Error list */}
        {errors.length > 0 && (
          <div className="overflow-y-auto border rounded p-2" style={{ maxHeight: '150px' }}>
            {errors.map((e, i) => (
              <div key={i} className="small text-warning">
                <span className="fw-semibold">{e.deviceName}:</span> {e.error}
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        {isComplete ? (
          <div className="d-flex justify-content-end">
            <button type="button" className="btn btn-primary px-4 rounded-4" onClick={onClose}>
              Done
            </button>
          </div>
        ) : (
          <p className="text-muted small mb-0">Please wait while devices are being added&hellip;</p>
        )}
      </div>
    </BaseModal>
  );
}